import { useCallback, useEffect, useRef, useState } from "react";
import Folienbuehne from "./Folienbuehne";
import { Kreuz } from "./Symbole";

/*
  Der Vorführmodus.

  ---------------------------------------------------------------
  Wofür das da ist
  ---------------------------------------------------------------

  Die Bürgerversammlung zum Bebauungsplan, die Klausurtagung, der Bericht in
  der Mitgliederversammlung. Zwei- bis dreimal im Jahr — aber dann steht
  jemand vor Publikum, und dann darf nichts überraschen.

  ---------------------------------------------------------------
  „Alles Bedienbare verschwindet" — und trotzdem herauskommen
  ---------------------------------------------------------------

  Im Entwurf stand, im Vorführmodus dürfe nichts Bedienbares zu sehen sein.
  Das ist richtig, solange man vorträgt: Ein Kreuz in der Ecke, das der
  Beamer mitzeigt, sieht unfertig aus.

  Buchstäblich genommen wäre es aber eine Falle. Escape gibt es auf dem
  iPad nicht, und das iPad ist hier der Hauptfall. Wer keinen Weg hinaus
  findet, muss die Anwendung schließen — vor Publikum.

  Deshalb: Die Bedienung ist unsichtbar und kommt bei jeder Bewegung oder
  Berührung für zweieinhalb Sekunden zurück. Während des Vortrags ist der
  Bildschirm leer; wer sucht, findet sofort.
*/

/* Wie lange die Bedienung nach einer Berührung sichtbar bleibt. */
const RUHE_MS = 2500;

/* Ab wie vielen Pixeln ein Wischen als Blättern zählt. */
const WISCH_SCHWELLE = 60;

export default function Vorfuehrung({ folien, start = 0, zugang, aufEnde }) {
  const [stelle, setzeStelle] = useState(start);
  const [zeigtBedienung, setzeZeigtBedienung] = useState(true);
  const flaecheRef = useRef(null);
  const ruheUhr = useRef(null);
  const wischStart = useRef(null);

  const anzahl = folien.length;

  const wecke = useCallback(() => {
    setzeZeigtBedienung(true);
    if (ruheUhr.current) clearTimeout(ruheUhr.current);
    ruheUhr.current = setTimeout(() => setzeZeigtBedienung(false), RUHE_MS);
  }, []);

  const weiter = useCallback(() => {
    setzeStelle((n) => Math.min(n + 1, anzahl - 1));
    wecke();
  }, [anzahl, wecke]);

  const zurueck = useCallback(() => {
    setzeStelle((n) => Math.max(n - 1, 0));
    wecke();
  }, [wecke]);

  /*
    Vollbild anfordern — und beim Verlassen wieder abgeben.

    Der Browser kann es verweigern; dann läuft die Vorführung eben im
    Fenster. Das ist kein Grund abzubrechen: Über den Beamer sieht man den
    Unterschied kaum, und ein Abbruch mit Fehlermeldung wäre vor Publikum
    das Schlechtere.
  */
  useEffect(() => {
    const flaeche = flaecheRef.current;
    if (flaeche && flaeche.requestFullscreen) {
      flaeche.requestFullscreen().catch(() => {});
    }

    /*
      Escape beendet in den meisten Browsern das Vollbild selbst, ohne dass
      ein Tastendruck bei uns ankommt. Deshalb hängt das Ende AM Vollbild,
      nicht nur an der Taste.
    */
    const beiWechsel = () => {
      if (!document.fullscreenElement) aufEnde();
    };
    document.addEventListener("fullscreenchange", beiWechsel);

    return () => {
      document.removeEventListener("fullscreenchange", beiWechsel);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [aufEnde]);

  /* Die gewohnten Tasten. Leertaste und Bild-ab, weil Präsentationsfernbedienungen genau die senden. */
  useEffect(() => {
    const beiTaste = (e) => {
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
        case " ":
        case "PageDown":
          e.preventDefault();
          weiter();
          break;
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp":
          e.preventDefault();
          zurueck();
          break;
        case "Home":
          e.preventDefault();
          setzeStelle(0);
          wecke();
          break;
        case "End":
          e.preventDefault();
          setzeStelle(anzahl - 1);
          wecke();
          break;
        case "Escape":
          e.preventDefault();
          aufEnde();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", beiTaste);
    return () => window.removeEventListener("keydown", beiTaste);
  }, [weiter, zurueck, wecke, anzahl, aufEnde]);

  useEffect(() => {
    wecke();
    return () => {
      if (ruheUhr.current) clearTimeout(ruheUhr.current);
    };
  }, [wecke]);

  function beiZeigerRunter(e) {
    wischStart.current = { x: e.clientX, y: e.clientY };
    wecke();
  }

  /*
    Wischen blättert. Waagerecht muss deutlich mehr sein als senkrecht —
    sonst blättert ein Daumen, der nur abrutscht.
  */
  function beiZeigerHoch(e) {
    const anfang = wischStart.current;
    wischStart.current = null;
    if (!anfang) return;

    const dx = e.clientX - anfang.x;
    const dy = e.clientY - anfang.y;
    if (Math.abs(dx) < WISCH_SCHWELLE || Math.abs(dx) < Math.abs(dy)) return;

    if (dx < 0) weiter();
    else zurueck();
  }

  const folie = folien[stelle] ? folien[stelle].folie : null;

  return (
    <div
      className="vorfuehrung"
      ref={flaecheRef}
      onPointerDown={beiZeigerRunter}
      onPointerUp={beiZeigerHoch}
      onPointerMove={() => {
        if (!zeigtBedienung) wecke();
      }}
    >
      <div className="vorfuehrung__blatt">
        <Folienbuehne folie={folie} zugang={zugang} nurLesen />
      </div>

      <div
        className={`vorfuehrung__bedienung ${zeigtBedienung ? "" : "vorfuehrung__bedienung--still"}`}
        /* Nicht mitwischen: Ein Tipp auf einen Knopf soll nicht auch blättern. */
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <button
          className="vorfuehrung__knopf"
          onClick={zurueck}
          disabled={stelle === 0}
          aria-label="Vorherige Folie"
        >
          ‹
        </button>

        <span className="vorfuehrung__zaehler" aria-live="polite">
          Folie {stelle + 1} von {anzahl}
        </span>

        <button
          className="vorfuehrung__knopf"
          onClick={weiter}
          disabled={stelle >= anzahl - 1}
          aria-label="Nächste Folie"
        >
          ›
        </button>

        <button className="vorfuehrung__knopf" onClick={aufEnde} aria-label="Vorführung beenden">
          <Kreuz groesse={18} />
        </button>
      </div>
    </div>
  );
}
